import { ChevronLeftIcon, PlayIcon } from '@heroicons/react/24/outline';
import { FacebookPhotoNotice } from './FacebookPhotoNotice';

interface PhotoGalleryOverlayProps {
    mediaUrls: string[];
    facebookPostUrl?: string;
    onBack: () => void;
    onOpenLightbox: (index: number) => void;
}

const isVideoUrl = (url: string) => {
    const cleanUrl = url.split('?')[0].toLowerCase();
    return cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.mov');
};

const galleryTilePattern = [
    'col-span-1 row-span-2 sm:col-span-3 sm:row-span-2',
    'col-span-1 row-span-2 sm:col-span-3 sm:row-span-2',
    'col-span-1 row-span-3 sm:col-span-2 sm:row-span-3',
    'col-span-1 row-span-2 sm:col-span-2 sm:row-span-2',
    'col-span-1 row-span-2 sm:col-span-2 sm:row-span-3',
    'col-span-1 row-span-2 sm:col-span-3 sm:row-span-2',
    'col-span-1 row-span-2 sm:col-span-3 sm:row-span-2',
    'col-span-1 row-span-2 sm:col-span-2 sm:row-span-2',
    'col-span-1 row-span-2 sm:col-span-2 sm:row-span-3',
    'col-span-1 row-span-3 sm:col-span-2 sm:row-span-2',
];

function GalleryTile({ url, index, onClick }: { url: string; index: number; onClick: () => void }) {
    const isVideo = isVideoUrl(url);

    return (
        <button
            type="button"
            onClick={onClick}
            className={`group relative block h-full w-full overflow-hidden rounded-lg bg-gray-100 text-left ${galleryTilePattern[index % galleryTilePattern.length]}`}
            aria-label={`Open photo ${index + 1}`}
        >
            {isVideo ? (
                <>
                    <video
                        src={url}
                        muted
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/10">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/65 text-white shadow-lg">
                            <PlayIcon className="h-6 w-6 fill-white" />
                        </span>
                    </span>
                </>
            ) : (
                <img
                    src={url}
                    alt={`Rental photo ${index + 1}`}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]"
                />
            )}
        </button>
    );
}

export function PhotoGalleryOverlay({ mediaUrls, facebookPostUrl, onBack, onOpenLightbox }: PhotoGalleryOverlayProps) {
    if (mediaUrls.length === 0) return null;

    return (
        <div className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-white text-gray-950">
            <div className="border-b border-gray-200 bg-white sm:sticky sm:top-0 sm:z-10">
                <div className="mx-auto flex max-w-6xl items-center px-4 py-4 sm:px-8">
                    <button
                        type="button"
                        onClick={onBack}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-900 hover:bg-gray-100"
                        aria-label="Back to rental"
                    >
                        <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                    <div className="flex-1 text-center text-sm font-semibold text-gray-900 sm:text-base">
                        {mediaUrls.length} photos
                    </div>
                    <div className="h-10 w-10" />
                </div>
            </div>

            <div className="mx-auto max-w-6xl px-4 py-4 sm:px-8 sm:py-8">
                {facebookPostUrl && (
                    <div className="mb-4">
                        <FacebookPhotoNotice postUrl={facebookPostUrl} />
                    </div>
                )}
                <div className="grid auto-rows-[92px] grid-cols-2 gap-2 sm:auto-rows-[120px] sm:grid-cols-6 sm:gap-3 lg:auto-rows-[150px]">
                    {mediaUrls.map((url, index) => (
                        <GalleryTile key={`${url}-${index}`} url={url} index={index} onClick={() => onOpenLightbox(index)} />
                    ))}
                </div>
            </div>
        </div>
    );
}
